import React, { useMemo, useState } from "react";
import Button from "../ui/Button";
import { formatDateTime } from "src/utils/date-time";
import ContactSupportModal from "../modals/ContactSupportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { LogOut } from "lucide-react";
import * as authActions from "src/redux/slices/authTenantSlice";
import { useNavigate } from "react-router-dom";
import { logoutAndRedirect } from "src/utils/auth";

interface PlanExpireBannerProps {
  type?: "admin" | "tenant";
}

const PlanExpireBanner: React.FC<PlanExpireBannerProps> = ({
  type = "tenant",
}) => {
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const {
    user: { data: tenantUser },
  } = useAppSelector((state) => state.authTenant);
  const dispatch = useAppDispatch();

  const planSummary = tenantUser?.planSummary;
  const planStatus = planSummary?.planStatus;
  const planEndsAt = planSummary?.plan?.planEndsAt;

  const daysToExpire = useMemo(() => {
    if (!planEndsAt) return null;
    const diffMs = new Date(planEndsAt).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [planEndsAt]);

  const handleLogout = () => {
    dispatch(authActions.logout());
    logoutAndRedirect({ navigate });
  };

  const showExpiryBanner =
    type === "tenant" &&
    planStatus === "active" &&
    planEndsAt &&
    daysToExpire !== null &&
    daysToExpire <= 10 &&
    daysToExpire >= 0;

  const showExpiredScreen = type === "tenant" && planStatus === "expired";
  const formattedPlanEndsAt = planEndsAt
    ? formatDateTime(planEndsAt, "DD/MM/YYYY hh:mm:ss A")
    : null;

  return (
    <>
      {/* Topbar Header */}
      {showExpiryBanner && (
        <div className="sticky top-0 z-30 border-b border-amber-200 bg-amber-50">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm text-amber-900">
            <span>
              Your plan will be expired on {formattedPlanEndsAt || "soon"}
            </span>
            <Button
              variant="outline"
              color="warning"
              onClick={() => setIsSupportModalOpen(true)}
            >
              Extend Plan
            </Button>
          </div>
        </div>
      )}

      {/* full Screen Banner */}
      {showExpiredScreen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white/95 p-6">
          <div className="max-w-lg text-center space-y-4">
            <h2 className="text-2xl font-semibold text-gray-900">
              Your plan expired kindly renew to continue
            </h2>
            <p className="text-sm text-gray-600">
              Please contact support to renew your plan and regain access.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button onClick={() => setIsSupportModalOpen(true)}>Renew</Button>

              <Button variant="primary" color="danger" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Sign out</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <ContactSupportModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </>
  );
};

export default PlanExpireBanner;
